import React, {
  createContext,
  useContext,
  useMemo,
  PropsWithChildren,
} from "react";
import { EventTracker } from "./EventTracker";
import { PendoEventTracker } from "./implementations/PendoEventTracker";

interface EventTrackingContextValue {
  tracker: EventTracker;
}

const EventTrackingContext = createContext<
  EventTrackingContextValue | undefined
>(undefined);

interface EventTrackingProviderProps extends PropsWithChildren {
  tracker?: EventTracker;
}

/**
 * EventTrackingProvider
 *
 * React context provider that supplies the EventTracker implementation
 * to components. Defaults to PendoEventTracker if no tracker is provided.
 */
export function EventTrackingProvider(
  props: EventTrackingProviderProps
): React.ReactElement {
  const { children, tracker } = props;

  // Default to PendoEventTracker if no tracker is provided
  const defaultTracker = useMemo(() => new PendoEventTracker(), []);

  const value = useMemo(
    () => ({
      tracker: tracker ?? defaultTracker,
    }),
    [tracker, defaultTracker]
  );

  return (
    <EventTrackingContext.Provider value={value}>
      {children}
    </EventTrackingContext.Provider>
  );
}

/**
 * Hook to access the EventTrackingContext
 * @throws Error if used outside of EventTrackingProvider
 */
export function useEventTrackingContext(): EventTrackingContextValue {
  const context = useContext(EventTrackingContext);
  if (context === undefined) {
    throw new Error(
      "useEventTrackingContext must be used within an EventTrackingProvider"
    );
  }
  return context;
}
